import React, { useState, useEffect } from "react";
import Router from "next/router";
import { useDispatch } from "react-redux";
import { bool, shape, array } from "prop-types";

import Basket from "@components/Basket/Basket";
import EmptyBasket from "@components/Basket/EmptyBasket";
import Layout from "@components/Layout/Layout";

import { ROUTES } from "@shared/routing/routes";
import { DEAL_STATES, IS_SERVER } from "@shared/constants";
import { universalGetLocalizedPathname } from "@lib/get-initial-props/universalGetLocalizedPathname";
import { withTranslation, i18n } from "@lib/i18n";
import RoutingUtils from "@shared/routing/utils";
import { getIsProductionMode } from "@lib/getIsProductionMode";

import api from "@api";

import {
  setB2BLeasysAction,
  setIsSelectionConfirmedAction,
  setJourneyAction,
} from "@redux/actions/dealActions";
import { setIsKeyAccountAction } from "@redux/actions/authActions";

const BasketPage = ({ pageProps, isSSRRendered }) => {
  const {
    isSelectionConfirmed,
    isServerRequest,
    deliveryPoints,
    isKeyAccount,
    isB2BLeasysSelected,
  } = pageProps;

  const [isEmptyBasketDisabled, setIsEmptyBasketDisabled] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isServerRequest) {
      dispatch(setIsSelectionConfirmedAction(isSelectionConfirmed));
      dispatch(setIsKeyAccountAction(!!isKeyAccount));
      dispatch(setB2BLeasysAction(!!isB2BLeasysSelected));
    }
  }, []);

  return (
    <Layout
      isGetPageNameByPathname
      withCEESubsidyPanel
      withFinancialPanel
      withSave={isSelectionConfirmed}
    >
      {isSelectionConfirmed || isEmptyBasketDisabled ? (
        <Basket
          deliveryPoints={deliveryPoints}
          onReturnButtonClick={() => setIsEmptyBasketDisabled(true)}
          isSSRRendered={isSSRRendered}
        />
      ) : (
        <EmptyBasket isSSRRendered={isSSRRendered} />
      )}
    </Layout>
  );
};

BasketPage.getInitialProps = async ctx => {
  const i18nInstance = IS_SERVER ? ctx.req.i18n : i18n;

  const { data } = await api.getDeliveryPoints(i18nInstance.language);
  const deliveryPoints = data ?? [];

  return IS_SERVER
    ? serverGetInitialProps(ctx, deliveryPoints)
    : clientGetInitialProps(ctx, deliveryPoints);
};

const serverGetInitialProps = async (ctx, deliveryPoints) => {
  const { deal } = ctx.store.getState();
  const currentDealState = deal?.currentDeal?.dealState;

  const { session } = ctx.req;
  const {
    isSelectionConfirmed,
    journey,
    isKeyAccount,
    isB2BLeasysSelected,
  } = session;

  ctx.store.dispatch(setJourneyAction(journey));

  if (currentDealState === DEAL_STATES.OFFER) {
    const debug = require("debug")("app:ssr-basket-page");

    debug(
      `🚀 BasketPage was accessed with current deal state "offer". Redirecting to: ${ROUTES.OFFER_SUMMARY}`
    );

    const offerSummaryRoute = universalGetLocalizedPathname(
      ctx,
      ROUTES.OFFER_SUMMARY
    );

    return ctx.res.redirect(offerSummaryRoute);
  }

  return {
    namespacesRequired: ["common"],
    isSelectionConfirmed,
    isServerRequest: true,
    deliveryPoints,
    isKeyAccount,
    isB2BLeasysSelected,
  };
};

const clientGetInitialProps = (ctx, deliveryPoints) => {
  const { deal, appConfig } = ctx.store.getState();
  const currentDealState = deal?.currentDeal?.dealState;
  const routingUtils = new RoutingUtils({
    routes: appConfig.routes,
  });

  const isProduction = getIsProductionMode();

  const isSelectionConfirmed = deal.isSelectionConfirmed;

  if (currentDealState === DEAL_STATES.OFFER) {
    if (!isProduction) {
      console.log(
        `🚀 BasketPage was accessed with current deal state "offer". Redirecting to: ${ROUTES.OFFER_SUMMARY}`
      );
    }

    return Router.push(
      ROUTES.OFFER_SUMMARY,
      routingUtils.getLocalizedPathname(
        ROUTES.OFFER_SUMMARY,
        null,
        i18n.language
      )
    );
  }

  return {
    namespacesRequired: ["common"],
    isSelectionConfirmed,
    deliveryPoints,
  };
};

BasketPage.propTypes = {
  pageProps: shape({
    deliveryPoints: array,
    isKeyAccount: bool,
  }),
  isSSRRendered: bool,
};

export default withTranslation("common")(BasketPage);
